import { useEffect, useState } from 'preact/hooks';
import { DashboardApi, HttpError, type WebhookCreated, type WebhookSubscription } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface EventKind {
  kind: string;
  description: string;
}

export function Webhooks({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [hooks, setHooks] = useState<WebhookSubscription[] | null>(null);
  const [kinds, setKinds] = useState<EventKind[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(['*']));
  const [created, setCreated] = useState<WebhookCreated | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.listWebhooks(org.id);
      setHooks(r.webhooks);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
      setHooks([]);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const k = await api.listWebhookEventKinds();
        setKinds(k.kinds.map((x) => ({ kind: x.kind, description: x.description })));
      } catch {
        /* dev environments without backend won't have this */
      }
    })();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh closes over org.id only
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const toggle = (k: string) => {
    const next = new Set(selected);
    if (k === '*') {
      next.clear();
      next.add('*');
    } else {
      next.delete('*');
      if (next.has(k)) next.delete(k);
      else next.add(k);
      if (next.size === 0) next.add('*');
    }
    setSelected(next);
  };

  const create = async () => {
    setCreating(true);
    try {
      const events = Array.from(selected);
      const c = await api.createWebhook(org.id, { url: url.trim(), events });
      setCreated(c);
      setUrl('');
      setSelected(new Set(['*']));
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this webhook? Pending deliveries will stop.')) return;
    try {
      await api.deleteWebhook(org.id, id);
      await refresh();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Webhooks</h1>
        <p>
          Subscribe to events from {org.name}. Each delivery is signed with HMAC-SHA256 in the
          <code>x-wholisphere-signature</code> header (Stripe-compatible scheme).
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      {created && (
        <div class="card key-created">
          <h2>New webhook — copy the secret now</h2>
          <p class="desc">
            This is the <strong>only time</strong> we show the signing secret. Store it before
            closing.
          </p>
          <div class="key-row">
            <code class="key-plaintext">{created.secret}</code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(created.secret)}
            >
              Copy
            </button>
          </div>
          <button type="button" class="secondary" onClick={() => setCreated(null)}>
            I've saved it
          </button>
        </div>
      )}

      <div class="card">
        <h2>Create a webhook</h2>
        <div class="field">
          <label htmlFor="webhook-url">URL</label>
          <input
            id="webhook-url"
            type="url"
            value={url}
            placeholder="https://your-server.example.com/wholisphere"
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
          />
        </div>
        <fieldset class="field" style={{ border: 0, padding: 0 }}>
          <legend>Subscribe to events</legend>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <input type="checkbox" checked={selected.has('*')} onChange={() => toggle('*')} />{' '}
            <strong>All events (*)</strong>
          </label>
          {kinds.map((k) => (
            <label key={k.kind} style={{ display: 'block', marginLeft: 20, marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={selected.has(k.kind)}
                onChange={() => toggle(k.kind)}
                disabled={selected.has('*')}
              />{' '}
              <code>{k.kind}</code> <span class="small muted">— {k.description}</span>
            </label>
          ))}
        </fieldset>
        <button
          type="button"
          disabled={creating || !url.trim() || selected.size === 0}
          onClick={() => void create()}
        >
          {creating ? 'Creating…' : 'Create webhook'}
        </button>
      </div>

      <div class="card">
        <h2>Existing webhooks</h2>
        {hooks === null ? (
          <p class="muted">
            <output>Loading…</output>
          </p>
        ) : hooks.length === 0 ? (
          <p class="muted">No webhooks yet.</p>
        ) : (
          <table class="audit">
            <thead>
              <tr>
                <th>URL</th>
                <th>Events</th>
                <th>Last delivery</th>
                <th>Failures</th>
                <th>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {hooks.map((w) => (
                <tr key={w.id}>
                  <td>
                    <code class="meta">{w.url}</code>
                  </td>
                  <td>
                    {w.events.map((e) => (
                      <span key={e} class="kind-pill" style={{ marginRight: 4 }}>
                        {e}
                      </span>
                    ))}
                  </td>
                  <td>
                    {w.lastDeliveryAt ? new Date(w.lastDeliveryAt).toLocaleString() : '—'}{' '}
                    {w.lastDeliveryStatus && (
                      <span
                        class="kind-pill"
                        style={{
                          marginLeft: 4,
                          color:
                            w.lastDeliveryStatus === 'success' ? 'var(--success)' : 'var(--danger)',
                        }}
                      >
                        {w.lastDeliveryStatus}
                      </span>
                    )}
                  </td>
                  <td>{w.failureCount}</td>
                  <td>
                    <button type="button" class="secondary" onClick={() => void remove(w.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
